/**
 * LandGrid.AI Educational Explorer - Main Entry Point
 * A web-based educational tool for exploring Earth's biomes through an interactive grid
 */

import './styles/main.css';
import { MapController } from './modules/map.js';
import { GridSystem } from './modules/grid.js';
import { BiomeService } from './modules/biome.js';
import { UIController } from './modules/ui.js';
import { EducationEngine } from './modules/education.js';
import { AccessibilityManager } from './modules/accessibility.js';
import { ClimateService } from './modules/climate.js';
import { AttributionService } from './modules/attribution.js';
import { OfflineContentManager } from './modules/offline.js';
import { UserAnalytics } from './modules/analytics.js';
import { APIService } from './modules/api.js';
import { ErrorHandler } from './utils/errorHandler.js';

/**
 * Main Application Class
 * Orchestrates all modules and manages the application lifecycle
 */
class LandGridApp {
  constructor() {
    this.modules = {};
    this.initialized = false;
    this.errorHandler = new ErrorHandler();
  }

  /**
   * Initialize the application
   */
  async init() {
    try {
      this.showLoading();

      // Initialize core services
      await this.initializeModules();

      // Set up event listeners
      this.setupEventListeners();

      // Load initial data
      await this.loadInitialData();

      this.hideLoading();
      this.initialized = true;

      // Initialize production monitoring
      if (process.env.NODE_ENV === 'production') {
        this.initializeProductionMonitoring();
      }

      console.log('LandGrid.AI Educational Explorer initialized successfully');
    } catch (error) {
      this.errorHandler.handleError(error, 'Failed to initialize application');
      this.hideLoading();
    }
  }

  /**
   * Initialize all application modules
   */
  async initializeModules() {
    try {
      // Initialize services in dependency order
      console.log('Initializing core services...');
      this.modules.apiService = new APIService();
      this.modules.biomeService = new BiomeService();
      this.modules.climateService = new ClimateService();
      this.modules.gridSystem = new GridSystem();

      console.log('Initializing map and UI components...');
      this.modules.mapController = new MapController('map');
      this.modules.uiController = new UIController();
      this.modules.educationEngine = new EducationEngine();
      this.modules.accessibilityManager = new AccessibilityManager();
      this.modules.attributionService = new AttributionService();

      console.log('Initializing offline and analytics services...');
      this.modules.offlineContentManager = new OfflineContentManager();
      this.modules.userAnalytics = new UserAnalytics();

      // Initialize API service first
      console.log('Initializing API service...');
      await this.modules.apiService.initialize();

      // Update error handler with offline mode status
      // Note: ErrorHandler starts with offline mode true to prevent early dialogs
      this.errorHandler.setOfflineMode(this.modules.apiService.isOfflineMode());

      // Connect modules
      console.log('Connecting modules...');
      this.connectModules();

    } catch (error) {
      console.error('Module initialization failed:', error);
      throw new Error(`Failed to initialize modules: ${error.message}`);
    }
  }

  /**
   * Connect modules with each other for communication
   */
  connectModules() {
    try {
      // Connect API service to modules that need backend data
      this.modules.biomeService?.setAPIService?.(this.modules.apiService);
      this.modules.gridSystem?.setAPIService?.(this.modules.apiService);
      this.modules.educationEngine?.setAPIService?.(this.modules.apiService);

      // Map receives biome and grid data
      if (this.modules.mapController) {
        this.modules.mapController.setBiomeService?.(this.modules.biomeService);
        this.modules.mapController.setGridSystem?.(this.modules.gridSystem);
        this.modules.mapController.setAPIService?.(this.modules.apiService);
      }

      // UI controls map and education engine
      if (this.modules.uiController) {
        this.modules.uiController.setMapController?.(this.modules.mapController);
        this.modules.uiController.setEducationEngine?.(this.modules.educationEngine);
        this.modules.uiController.setBiomeService?.(this.modules.biomeService);
        this.modules.uiController.setAPIService?.(this.modules.apiService);
      }

      // Education engine uses biome service and offline content
      if (this.modules.educationEngine) {
        this.modules.educationEngine.setBiomeService?.(this.modules.biomeService);
        this.modules.educationEngine.setOfflineContentManager?.(this.modules.offlineContentManager);
        this.modules.educationEngine.setUserAnalytics?.(this.modules.userAnalytics);
        this.modules.educationEngine.setAPIService?.(this.modules.apiService);
      }

      // UI controller can access offline content and analytics
      if (this.modules.uiController) {
        this.modules.uiController.setOfflineContentManager?.(this.modules.offlineContentManager);
        this.modules.uiController.setUserAnalytics?.(this.modules.userAnalytics);
      }

      // Map controller uses analytics for interaction tracking
      this.modules.mapController?.setUserAnalytics?.(this.modules.userAnalytics);

      // Accessibility manager works with all UI components
      if (this.modules.accessibilityManager) {
        this.modules.accessibilityManager.setUIController?.(this.modules.uiController);
        this.modules.accessibilityManager.setMapController?.(this.modules.mapController);
      }

      console.log('Modules connected successfully');
    } catch (error) {
      console.warn('Some module connections failed:', error);
      this.errorHandler.handleError(error, 'Module connection error');
    }
  }

  /**
   * Set up global event listeners
   */
  setupEventListeners() {
    // Error handling
    window.addEventListener('error', (event) => {
      this.errorHandler.handleError(event.error, 'Unhandled JavaScript error');
      this.modules.userAnalytics?.trackError(event.error, 'Unhandled JavaScript error', 'high');
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.errorHandler.handleError(event.reason, 'Unhandled promise rejection');
      this.modules.userAnalytics?.trackError(event.reason, 'Unhandled promise rejection', 'high');
    });

    // Resize handling
    window.addEventListener('resize', this.debounce(() => {
      if (this.modules.mapController) {
        this.modules.mapController.handleResize();
      }
    }, 250));

    // Keyboard shortcuts
    document.addEventListener('keydown', (event) => {
      this.handleKeyboardShortcuts(event);
    });

    // Offline/online status
    window.addEventListener('online', () => {
      console.log('Connection restored');
      this.modules.uiController?.showNotification('Connection restored', 'success');
    });

    window.addEventListener('offline', () => {
      console.log('Connection lost - entering offline mode');
      this.modules.uiController?.showNotification('You are now offline. Some features may be limited.', 'warning');
    });
  }

  /**
   * Load initial data required for the application
   */
  async loadInitialData() {
    // Check if we're in offline mode and notify user
    if (this.modules.apiService.isOfflineMode()) {
      console.log('ðŸ”¶ Running in offline mode - some features may be limited');
      // We'll show this notification after UI is initialized
    }

    // Load biome data
    await this.modules.biomeService.loadBiomeData();

    // Generate grid
    this.modules.gridSystem.generateGrid();

    // Initialize map with data
    await this.modules.mapController.initializeMap();

    // Set up UI components
    this.modules.uiController.initialize();

    // Show offline mode notification if applicable
    if (this.modules.apiService.isOfflineMode()) {
      this.modules.uiController?.showNotification?.(
        'Running in demo mode with local data. Quiz scores won\'t be saved.',
        'info',
        5000
      );
    }

    // Initialize attribution system
    this.modules.attributionService.initialize();

    // Initialize offline content system
    await this.modules.offlineContentManager.initialize();

    // Initialize analytics system
    await this.modules.userAnalytics.initialize();

    // Load educational content
    await this.modules.educationEngine.loadContent();

    // Track initial page view
    this.modules.userAnalytics.trackPageView('home', {
      app_version: '1.0.0',
      initialized_modules: Object.keys(this.modules).length
    });
  }

  /**
   * Handle keyboard shortcuts
   */
  handleKeyboardShortcuts(event) {
    // Reset view (R key)
    if (event.key.toLowerCase() === 'r' && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
      this.modules.mapController?.resetView();
    }

    // Screenshot (S key)
    if (event.key.toLowerCase() === 's' && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
      this.modules.uiController?.takeScreenshot();
    }

    // Toggle accessibility mode (A key)
    if (event.key.toLowerCase() === 'a' && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
      this.modules.accessibilityManager?.toggleHighContrast();
    }

    // Help (? key)
    if (event.key === '?') {
      event.preventDefault();
      this.modules.uiController?.showHelp();
    }

    // Attribution modal (I key)
    if (event.key.toLowerCase() === 'i' && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
      this.modules.attributionService?.showAttributionModal();
    }
  }

  /**
   * Show loading overlay
   */
  showLoading() {
    const loadingOverlay = document.getElementById('loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.classList.remove('hidden');
    }
  }

  /**
   * Hide loading overlay
   */
  hideLoading() {
    const loadingOverlay = document.getElementById('loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.classList.add('hidden');
      setTimeout(() => {
        loadingOverlay.style.display = 'none';
      }, 300);
    }
  }

  /**
   * Utility function to debounce function calls
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Initialize production monitoring and analytics
   */
  initializeProductionMonitoring() {
    try {
      // Error tracking
      window.addEventListener('error', (event) => {
        this.trackError('JavaScript Error', event.error, {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        });
      });

      // Unhandled promise rejection tracking
      window.addEventListener('unhandledrejection', (event) => {
        this.trackError('Unhandled Promise Rejection', event.reason);
      });

      // Performance tracking
      if ('performance' in window && 'PerformanceObserver' in window) {
        this.initializePerformanceTracking();
      }

      // Track Core Web Vitals
      this.trackWebVitals();

      console.log('âœ… Production monitoring initialized');
    } catch (error) {
      console.error('Failed to initialize production monitoring:', error);
    }
  }

  /**
   * Initialize performance tracking
   */
  initializePerformanceTracking() {
    // Track navigation timing
    window.addEventListener('load', () => {
      setTimeout(() => {
        const perfData = performance.getEntriesByType('navigation')[0];
        if (perfData) {
          this.trackPerformance('page_load', {
            load_time: perfData.loadEventEnd - perfData.loadEventStart,
            dom_content_loaded: perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart,
            first_byte: perfData.responseStart - perfData.requestStart
          });
        }
      }, 0);
    });

    // Track resource timing for large resources
    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.transferSize > 50000) { // Track resources > 50KB
          this.trackPerformance('large_resource', {
            name: entry.name,
            size: entry.transferSize,
            duration: entry.duration
          });
        }
      });
    });

    observer.observe({ entryTypes: ['resource'] });
  }

  /**
   * Track Core Web Vitals
   */
  trackWebVitals() {
    // Track Largest Contentful Paint (LCP)
    if ('PerformanceObserver' in window) {
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        this.trackPerformance('lcp', { value: lastEntry.startTime });
      });
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

      // Track First Input Delay (FID)
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          this.trackPerformance('fid', {
            value: entry.processingStart - entry.startTime
          });
        });
      });
      fidObserver.observe({ entryTypes: ['first-input'] });
    }

    // Track Cumulative Layout Shift (CLS)
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      }
    });
    clsObserver.observe({ entryTypes: ['layout-shift'] });

    // Report CLS when the page is hidden
    const reportCLS = () => {
      this.trackPerformance('cls', { value: clsValue });
    };

    window.addEventListener('pagehide', reportCLS);
    window.addEventListener('beforeunload', reportCLS);
  }

  /**
   * Track error events
   */
  trackError(type, error, metadata = {}) {
    if (!process.env.VITE_ENABLE_ERROR_REPORTING) return;

    const errorData = {
      type,
      message: error?.message || String(error),
      stack: error?.stack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      ...metadata
    };

    // In a real application, send to error tracking service
    console.error('Tracked Error:', errorData);
  }

  /**
   * Track performance metrics
   */
  trackPerformance(metric, data) {
    if (!process.env.VITE_ENABLE_ANALYTICS) return;

    const performanceData = {
      metric,
      value: data,
      timestamp: new Date().toISOString(),
      url: window.location.href
    };

    // In a real application, send to analytics service
    console.log('Performance Metric:', performanceData);
  }

  /**
   * Get application health status
   */
  getHealthStatus() {
    return {
      initialized: this.initialized,
      modules: Object.keys(this.modules).reduce((status, key) => {
        status[key] = !!this.modules[key];
        return status;
      }, {}),
      timestamp: new Date().toISOString()
    };
  }
}

// Initialize application when DOM is ready
function initializeApp() {
  const app = new LandGridApp();

  // Make app globally available for debugging
  window.LandGridApp = app;

  // Start initialization
  app.init().catch(error => {
    console.error('Failed to initialize LandGrid.AI:', error);
  });
}

// Start the application
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeApp);
} else {
  initializeApp();
}


